import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Card, CardContent, CardMedia, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useUser } from './UserContext';

const API_URL = process.env.REACT_APP_API_URL;

function MonitorPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Connecting...');
  const [photos, setPhotos] = useState([]);
  const [copiedGuid, setCopiedGuid] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  function copyGuid(guid) {
    navigator.clipboard.writeText(guid).then(() => {
      setCopiedGuid(guid);
      setTimeout(() => setCopiedGuid(null), 1500);
    });
  }

  useEffect(() => {
    if (!user?.isSysAdmin) {
      navigate('/');
      return;
    }

    // `alive` is a closure variable unique to this effect invocation.
    // When cleanup runs it's set to false, so onclose handlers from this
    // invocation won't reconnect even if the next invocation has already started.
    let alive = true;

    // Convert http(s):// to ws(s)://
    const wsUrl = API_URL.replace(/^http/, 'ws') + `/ws/monitor?t=${user.token}`;

    function connect() {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => setStatus('Connected');

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_photo') {
          setPhotos((prev) => [data, ...prev].slice(0, 200));
        }
      };

      ws.onclose = () => {
        if (!alive) return; // this invocation was cleaned up — do not reconnect
        setStatus('Disconnected — reconnecting...');
        reconnectTimer.current = setTimeout(connect, 5000);
      };

      ws.onerror = () => {
        setStatus('Connection error');
      };
    }

    connect();

    return () => {
      alive = false;
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [user, navigate]);

  const statusColor = status === 'Connected' ? 'success'
    : status.startsWith('Disconnected') ? 'warning'
    : 'error';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h5">Live Photo Monitor</Typography>
        <Chip label={status} color={statusColor} size="small" />
      </Box>

      {photos.length === 0 && (
        <Typography color="text.secondary">Waiting for uploads…</Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {photos.map((photo, i) => (
          <Card key={i} sx={{ width: 220 }}>
            <CardMedia
              component="img"
              image={photo.thumbUrl}
              alt={photo.photoTitle}
              sx={{ height: 160, objectFit: 'cover' }}
            />
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" noWrap>{photo.photoTitle}</Typography>
              <Typography variant="caption" display="block" color="text.secondary" noWrap>
                {photo.contestName}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                {new Date(photo.timestamp).toLocaleTimeString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {photo.photoGuid}
                </Typography>
                <Tooltip title={copiedGuid === photo.photoGuid ? 'Copied!' : 'Copy GUID'} placement="top">
                  <IconButton size="small" onClick={() => copyGuid(photo.photoGuid)} sx={{ ml: 0.5, p: 0.25 }}>
                    <ContentCopyIcon sx={{ fontSize: '0.75rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

export default MonitorPage;
